import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Button, RadioGroup, FormControlLabel, Radio,
  Paper, CircularProgress, LinearProgress, Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { QUESTIONNAIRES, resolveKey } from "../Assessments/questionnaires";

const API_BASE = "/api/v1";

export default function PublicAssessment() {
  const { customerId, assessmentKey } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | already_done | not_found | submitted | error
  const [qData, setQData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const q = QUESTIONNAIRES[resolveKey(assessmentKey)];
    if (!q) { setStatus("not_found"); return; }

    fetch(`${API_BASE}/public/assessment/${customerId}/${assessmentKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "completed") {
          setStatus("already_done");
        } else {
          setQData(q);
          setStatus("ready");
        }
      })
      .catch(() => {
        // API error (not assigned yet), but questionnaire exists — allow filling
        setQData(q);
        setStatus("ready");
      });
  }, [customerId, assessmentKey]);

  const handleAnswer = (questionId, optionIndex) => {
    const question = qData.questions.find(q => q.id === questionId);
    const score = question.options[Number(optionIndex)].score;
    setAnswers((prev) => ({ ...prev, [questionId]: score }));
    setSelectedOptions((prev) => ({ ...prev, [questionId]: Number(optionIndex) }));
  };

  const obtainedScore = Object.values(answers).reduce((s, v) => s + v, 0);
  const totalQuestions = qData?.questions?.length || 0;
  const answered = Object.keys(answers).length;
  const allAnswered = answered === totalQuestions;

  const getScoreLevel = (score) => {
    if (!qData?.scoring) return null;
    return qData.scoring.find((s) => score >= s.min && score <= s.max) || null;
  };

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/assessment/${customerId}/${assessmentKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assessment_key: assessmentKey,
          answers: selectedOptions,
          obtained_score: obtainedScore,
          total_score: qData.maxScore,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ score: obtainedScore, level: getScoreLevel(obtainedScore) });
        setStatus("submitted");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Screens ────────────────────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === "already_done") {
    return (
      <CenteredCard>
        <CheckCircleOutlineIcon sx={{ fontSize: "3.5rem", color: "#22c55e", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: "#111827" }}>Already Submitted</Typography>
        <Typography sx={{ mt: 0.75, color: "#6b7280", fontSize: "0.9rem", textAlign: "center" }}>
          This assessment has already been completed. Thank you!
        </Typography>
      </CenteredCard>
    );
  }

  if (status === "not_found") {
    return (
      <CenteredCard>
        <ErrorOutlineIcon sx={{ fontSize: "3.5rem", color: "#ef4444", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: "#111827" }}>Assessment Not Found</Typography>
        <Typography sx={{ mt: 0.75, color: "#6b7280", fontSize: "0.9rem", textAlign: "center" }}>
          This link is invalid or has expired. Please contact your counsellor.
        </Typography>
      </CenteredCard>
    );
  }

  if (status === "error") {
    return (
      <CenteredCard>
        <ErrorOutlineIcon sx={{ fontSize: "3.5rem", color: "#ef4444", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: "#111827" }}>Submission Failed</Typography>
        <Typography sx={{ mt: 0.75, color: "#6b7280", fontSize: "0.9rem", textAlign: "center" }}>
          Something went wrong. Please try again or contact your counsellor.
        </Typography>
        <Button variant="contained" sx={{ mt: 2, borderRadius: "8px", textTransform: "none" }} onClick={() => setStatus("ready")}>
          Try Again
        </Button>
      </CenteredCard>
    );
  }

  if (status === "submitted" && result) {
    const lvl = result.level;
    const colorMap = { green: "#22c55e", yellow: "#eab308", orange: "#f97316", red: "#ef4444" };
    const chipColor = colorMap[lvl?.color] || "#3b82f6";
    return (
      <CenteredCard>
        <CheckCircleOutlineIcon sx={{ fontSize: "3.5rem", color: "#22c55e", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: "#111827" }}>Assessment Submitted!</Typography>
        <Typography sx={{ mt: 0.5, color: "#6b7280", fontSize: "0.85rem" }}>Thank you for completing the assessment.</Typography>
        <Box sx={{ mt: 3, p: 2.5, borderRadius: "12px", bgcolor: "#f8fafc", border: "1px solid #e5e7eb", width: "100%", maxWidth: 340, textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af", mb: 0.5 }}>Your Score</Typography>
          <Typography sx={{ fontSize: "2.5rem", fontWeight: 800, color: "#111827", lineHeight: 1 }}>
            {result.score}<Typography component="span" sx={{ fontSize: "1rem", color: "#6b7280", fontWeight: 400 }}>/{qData?.maxScore}</Typography>
          </Typography>
          {lvl && (
            <Chip label={lvl.level} sx={{ mt: 1.5, fontWeight: 700, fontSize: "0.8rem", bgcolor: chipColor + "22", color: chipColor, border: `1px solid ${chipColor}55` }} />
          )}
          {lvl?.recommendation && (
            <Typography sx={{ mt: 1.25, fontSize: "0.78rem", color: "#6b7280" }}>{lvl.recommendation}</Typography>
          )}
        </Box>
        <Typography sx={{ mt: 2.5, fontSize: "0.8rem", color: "#9ca3af", textAlign: "center" }}>
          Your results have been shared with your counsellor.
        </Typography>
      </CenteredCard>
    );
  }

  // ── Main questionnaire ──────────────────────────────────────────────────────
  const progress = totalQuestions > 0 ? (answered / totalQuestions) * 100 : 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ maxWidth: 680, mx: "auto", mb: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111827" }}>Clinical Assessment</Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "#9ca3af" }}>Confidential — results shared only with your counsellor</Typography>
      </Box>

      {/* Card */}
      <Paper elevation={0} sx={{ maxWidth: 680, mx: "auto", borderRadius: "16px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {/* Title bar */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #f3f4f6" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box sx={{ fontSize: "1.8rem" }}>{qData.icon}</Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#111827" }}>{qData.name}</Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>{qData.instructions}</Typography>
            </Box>
          </Box>
          {/* Progress */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "#e5e7eb", "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" } }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", whiteSpace: "nowrap" }}>{answered}/{totalQuestions}</Typography>
          </Box>
        </Box>

        {/* Questions */}
        <Box sx={{ px: 3, py: 2.5 }}>
          {qData.questions.map((q, idx) => (
            <Box key={q.id} sx={{ mb: 3, pb: 3, borderBottom: idx < qData.questions.length - 1 ? "1px solid #f3f4f6" : "none" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: "#111827", mb: 1.25 }}>
                <Box component="span" sx={{ color: "#3b82f6", mr: 0.75 }}>{idx + 1}.</Box>
                {q.text}
              </Typography>
              <RadioGroup value={selectedOptions[q.id] !== undefined ? String(selectedOptions[q.id]) : ""} onChange={(e) => handleAnswer(q.id, e.target.value)}>
                {q.options.map((opt, optIdx) => (
                  <FormControlLabel
                    key={optIdx}
                    value={String(optIdx)}
                    control={<Radio size="small" sx={{ color: "#d1d5db", "&.Mui-checked": { color: "#3b82f6" } }} />}
                    label={<Typography sx={{ fontSize: "0.83rem", color: "#374151" }}>{opt.text}</Typography>}
                    sx={{ mb: 0.25, borderRadius: "8px", px: 1, mx: 0, "&:hover": { bgcolor: "#eff6ff" }, ...(selectedOptions[q.id] === optIdx ? { bgcolor: "#eff6ff" } : {}) }}
                  />
                ))}
              </RadioGroup>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
          <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af" }}>
            {allAnswered ? "All questions answered ✓" : `${totalQuestions - answered} question${totalQuestions - answered !== 1 ? "s" : ""} remaining`}
          </Typography>
          <Button
            variant="contained"
            disabled={!allAnswered || submitting}
            onClick={handleSubmit}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 3.5, py: 1.1, bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }, "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" } }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Submit Assessment"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

function CenteredCard({ children }) {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc", px: 2 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", border: "1px solid #e5e7eb", maxWidth: 420, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {children}
      </Paper>
    </Box>
  );
}
